import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import Select from '../Util/Form/CustomInputs/Select';
import DateField from '../Util/Form/CustomInputs/DateField';
import DirdExpense from './Reports/DirdExpense/DirdExpense';
import config from '../../../../config/config';
import './PhisicFinance.css';
import '../home/Home.css';
import moment from 'moment';
import ExpenseReportV1 from './Reports/ExpenseReportV1/ExpenseReportV1';
import ExpenseReportV2 from './Reports/ExpenseReportV2/ExpenseReportV2';
import DirdRevenueReport from './Reports/DirdRevenueReport/DirdRevenueReport';
import CashFlowReport from './Reports/CashFlowReport.js/CashFlowReport';
import { useKeysStore } from '../../../../../core/store/useKeysStore';
import Dird from './Reports/Dird/Dird';
import DirdFinancialBalance from './Reports/DirdFinancialBalance/DirdFinancialBalance';

const PhisicFinance = () => {
  const { keys } = useKeysStore();
  const sessionSelector = useSelector((state) => state.core.session);
  const friendly_url = sessionSelector.pdc_project_friendly_url;

  const [report, setReport] = useState(null);
  const [fields, setFields] = useState({
    accountProject: { value: '', error: '' },
    reportType: { value: '', error: '' },
    originResource: { value: '', error: '' },
    startDate: { value: moment().format('YYYY-MM-DD'), error: '' },
    endDate: { value: '', error: '' }
  });

  const endpoints = useMemo(() => ({
    enpointGetAllAccountProjects: `${config.endpoints.backend}portal/getallaccountprojects`,
    enpointGetAllReportTypes: `${config.endpoints.backend}portal/getallreporttypes`,
    enpointGetAllOriginResources: `${config.endpoints.backend}portal/getalloriginresources`,
  }), []);

  const validateDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = parseInt((startDate.split('-').join('')));
    const end = parseInt((endDate.split('-').join('')));
    return start > end ? 'Data inválida!' : null;
  };

  const validateFields = (fields) => {
    const errors = {
      accountProject: { value: fields.accountProject.value, error: '' },
      reportType: { value: fields.reportType.value, error: '' },
      originResource: { value: fields.originResource.value, error: '' },
      startDate: { value: fields.startDate.value, error: '' },
      endDate: { value: fields.endDate.value, error: '' },
    };
    const requiredMessage = 'Este campo é obrigatório!';
  
    if (!fields.reportType.value) errors.reportType.error = requiredMessage;
    if (!fields.startDate.value) errors.startDate.error = requiredMessage;
    if (!fields.endDate.value) errors.endDate.error = requiredMessage;
  
    const dateError = validateDateRange(fields.startDate.value, fields.endDate.value);
    if (dateError) errors.endDate.error = dateError;

    return errors;
  };

  const checkForm = useCallback(() => {
    const validationErrors = validateFields(fields);
    setFields((prevFields) => ({ ...prevFields, ...validationErrors }));
    return { verify: Object.entries(validationErrors).find(([key, field]) => field.error !== '') ? false : true, values: fields };
  }, [fields]);

  const fetchReportData = async () => {
    setReport(<div></div>);
    const formValidation = checkForm();
    if (formValidation.verify) {
      const filters = prepareValuesToSubmit();
      switch (fields.reportType.value) {
        case keys?.default_foreign_key?.pdc_report_types?.dird:
          setReport(<Dird filters={filters} />);
          break;
        case keys?.default_foreign_key?.pdc_report_types?.financial_balance:
          setReport(<DirdFinancialBalance filters={filters} />);
          break;
        case keys?.default_foreign_key?.pdc_report_types?.payable_dird:
          setReport(<DirdExpense filters={filters} />);
          break;
        case keys?.default_foreign_key?.pdc_report_types?.revenue_relationv1:
          setReport(<ExpenseReportV1 filters={filters} />);
          break;
        case keys?.default_foreign_key?.pdc_report_types?.revenue_relationv2:
          setReport(<ExpenseReportV2 filters={filters} />);
          break;
        case keys?.default_foreign_key?.pdc_report_types?.revenue_dird:
          setReport(<DirdRevenueReport filters={filters} />);
          break;
        case keys?.default_foreign_key?.pdc_report_types?.cash_flow:
          setReport(<CashFlowReport filters={filters} />);
          break;
        default:
          break;
      }
    }
  };

  const prepareValuesToSubmit = () => {
    let values = {
      accountProject: fields.accountProject.value,
      reportValue: fields.reportType.value,
      originResource: fields.originResource.value,
      startDate: fields.startDate.value,
      endDate: fields.endDate.value,
      friendly_url
    }

    return values;
  }

  return (
    <div>
      <section className="content" style={{ marginTop: '30px' }}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card card-outline card-info cardReport">
                <div className="card-header">
                  <h3 className="card-title"><i className="fa-solid fa-pen-to-square mr-1"></i>Relatórios</h3>
                </div>
                <div className='sectionReport'>
                  <form id='formReport'>
                    <div className='row mb-2'>
                      <div className='col-3'>
                        <div key='filterAccountProject'>
                          <label htmlFor={'id_accountProject'}>
                            Projeto
                          </label>
                          <Select
                            name={'accountProject'}
                            value={fields.accountProject.value}
                            error={fields.accountProject.error}
                            onChange={(e) => setFields(prevFields => ({ ...prevFields, accountProject: { value: e.target.value, error: '' } }))}
                            optionsEndpoint={{
                              endpoint: endpoints.enpointGetAllAccountProjects,
                              label: 'pdc_project_name',
                              value: 'pdc_project_name',
                              params: {
                                filter: {
                                  friendly_url
                                }
                              }
                            }}
                            options={[]}
                          />
                        </div>
                      </div>
                      <div className='col-3'>
                        <div key='filterReportType'>
                          <label htmlFor={'id_' + 'reportType'}>
                            Tipo do relatório
                          </label>
                          <span className="required"> *</span>
                          <Select
                            name={'reportType'}
                            value={fields.reportType.value}
                            error={fields.reportType.error}
                            onChange={(e) => setFields(prevFields => ({ ...prevFields, reportType: { value: e.target.value, error: '' } }))}
                            optionsEndpoint={{
                              endpoint: endpoints.enpointGetAllReportTypes,
                              label: 'name',
                              value: 'id',
                            }}
                            options={[]}
                          />
                        </div>
                      </div>
                      <div className='col-3'>
                        <div>
                          <label htmlFor={'id_' + 'originResource'}>
                            Origem dos recursos
                          </label>
                          <Select
                            name={'originResource'}
                            value={fields.originResource.value}
                            error={fields.originResource.error}
                            onChange={(e) => setFields(prevFields => ({ ...prevFields, originResource: { value: e.target.value, error: '' } }))}
                            optionsEndpoint={{
                              endpoint: endpoints.enpointGetAllOriginResources,
                              label: 'description',
                              value: 'id',
                              params: {
                                filter: {
                                  friendly_url
                                }
                              }
                            }}
                            options={[]}
                          />
                        </div>
                      </div>
                      <div className='col-2'>
                        <div className="row">
                          <div className="col-6">
                            <label htmlFor='startDate'>De</label>
                            <span className="required"> *</span>
                            <DateField
                              name='startDate'
                              value={fields.startDate.value}
                              onChange={(e) => setFields(prevFields => ({ ...prevFields, startDate: { value: e.target.value === 'Data inválida' ? '' : e.target.value, error: '' } }))}
                              error={fields.startDate.error}
                              />
                            </div>
                            <div className="col-6">
                              <label htmlFor='endDate'>Até</label>
                              <span className="required"> *</span>
                              <DateField
                                name='endDate'
                                value={fields.endDate.value}
                                onChange={(e) => setFields(prevFields => ({ ...prevFields, endDate: { value: e.target.value === 'Data inválida' ? '' : e.target.value, error: '' } }))}
                                error={fields.endDate.error}
                              />
                            </div>
                          </div>
                        </div>
                        <div className='col-2'>
                          <label>&nbsp;</label>
                          <div>
                            <Button style={{ width: 'auto' }} onClick={fetchReportData}>Gerar Relatório</Button>
                          </div>
                        </div>
                      </div>
                    </form>
  
                    <div className='sectionReport' id='report'>
                      {report}
                    </div>
  
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  };
  
  export default PhisicFinance;
  